/**
 * duplicateFollowUpSend — decides whether a follow-up send repeats a step
 * we already sent to this address.
 *
 * An intro (or any earlier step) is not a duplicate of the step being
 * composed. Follow-up 1 after an intro stays quiet. The warning fires only
 * when this same stage, or this same template within that stage, already
 * went to the address.
 */

const STAGE_BY_LABEL = {
  intro: 'intro',
  introduction: 'intro',
  'introduce yourself': 'intro',
  follow_up_1: 'follow_up_1',
  'follow up': 'follow_up_1',
  'first follow-up': 'follow_up_1',
  'first follow up': 'follow_up_1',
  '1. follow-up': 'follow_up_1',
  '1. follow up': 'follow_up_1',
  follow_up_2: 'follow_up_2',
  'second follow up': 'follow_up_2',
  'second follow-up': 'follow_up_2',
  '2. follow-up': 'follow_up_2',
  '2. follow up': 'follow_up_2',
  follow_up_3: 'follow_up_3',
  'third follow up': 'follow_up_3',
  'third follow-up': 'follow_up_3',
  '3. follow-up': 'follow_up_3',
  '3. follow up': 'follow_up_3',
};

export const extractEmailAddress = (value) => {
  if (!value) return '';
  const raw = Array.isArray(value) ? value.find(Boolean) : value;
  if (!raw) return '';
  const text = String(raw);
  const angled = text.match(/<([^>]+)>/);
  return (angled ? angled[1] : text).replace(/\s+/g, '').trim().toLowerCase();
};

// Map a stored emailType or a topic label onto intro / follow_up_1 / _2 / _3.
// Anything we cannot place (unknown, a bare "follow_up", a custom name) stays
// empty so it cannot masquerade as a specific step.
export const canonicalStage = (value) => {
  const key = String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!key || key === 'unknown') return '';
  if (STAGE_BY_LABEL[key]) return STAGE_BY_LABEL[key];
  const compact = key.replace(/[\s-]+/g, '_');
  if (STAGE_BY_LABEL[compact]) return STAGE_BY_LABEL[compact];
  const numbered = compact.match(/^follow_?up_?([123])$/);
  if (numbered) return `follow_up_${numbered[1]}`;
  return '';
};

const stageOf = (send) => (
  canonicalStage(send?.emailType)
  || canonicalStage(send?.templateName)
  || canonicalStage(send?.topicName)
);

/**
 * Prior sends that repeat the follow-up currently being composed.
 * `current` is `{ email, stage, topicName, templateId }`.
 * Returns the matches newest-first when the input is newest-first.
 */
export const duplicateFollowUpSends = (priorSends, current = {}) => {
  const addr = extractEmailAddress(current.email);
  const currentStage = canonicalStage(current.stage) || canonicalStage(current.topicName);
  if (!currentStage || currentStage === 'intro') return [];
  const templateId = current.templateId || '';

  return (priorSends || []).filter((prior) => {
    const priorAddr = extractEmailAddress(prior?.to);
    if (addr && priorAddr && priorAddr !== addr) return false;

    const priorStage = stageOf(prior);
    if (priorStage && priorStage === currentStage) return true;

    if (templateId && prior?.templateId && prior.templateId === templateId) {
      // A template reused from a different known step (the intro, or follow-up
      // 1 while composing follow-up 2) is not this step.
      if (priorStage && priorStage !== currentStage) return false;
      return true;
    }
    return false;
  });
};
